import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfigureAssignmentModal from '../../components/tasks/ConfigureAssignmentModal';
import LinkedCasesActionButton from '../../components/tasks/LinkedCasesActionButton';
import LinkedCasesModal from '../../components/tasks/LinkedCasesModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { EscalationTaskRow } from '../../data/escalationTasks';
import { EMPTY_CELL } from '../../data/recordRoom';
import {
  assignEscalationUserTask,
  fetchEscalationAssignedColumnSuggestions,
  fetchEscalationUnassignedColumnSuggestions,
  listAssignedEscalationTasks,
  listUnassignedEscalationTasks,
} from '../../services/escalationTaskAssignment';
import { setFlashToast } from '../../utils/flashToast';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { escalationTasksReturn } from '../task-case/taskCaseDetailsReturnNavigation';
import { notificationRowHighlightClass } from '../../utils/taskRowHighlight';

function urgencyPill(u: EscalationTaskRow['urgency']): string {
  if (!u) return '';
  if (u === 'High') return 'pill p-urg-h';
  if (u === 'Medium') return 'pill p-urg-m';
  return 'pill p-urg-l';
}

function urgencyLabel(u: EscalationTaskRow['urgency']): string {
  return u ?? EMPTY_CELL;
}

/**
 * Escalation Tasks — Unassigned: POST `.../escalation-tasks/unassign-task-case-requests`.
 * Assigned: POST `.../escalation-tasks/assign-task-case-requests` (fetched when the Assigned tab is selected).
 * Configure Assignment: CMS-CCA-1 / CMS-CCA-2 dropdowns; save POST `.../escalation-tasks/assign-user-task`.
 */
export default function EscalationTasksPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'assigned' ? 'assigned' : 'unassigned';
  const [tab, setTab] = useState<'unassigned' | 'assigned'>(initialTab);
  const taskCaseReturn = useMemo(() => escalationTasksReturn(tab), [tab]);
  const { canUpdate, taskCaseLink } = useTaskCaseDetailsNavigation({ returnState: taskCaseReturn });

  const [unassignedRows, setUnassignedRows] = useState<EscalationTaskRow[]>([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedTotalPages, setUnassignedTotalPages] = useState(1);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [unassignedPageSize, setUnassignedPageSize] = useState(10);
  const [unassignedFilters, setUnassignedFilters] = useState<Record<string, string>>({});
  const [unassignedSort, setUnassignedSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'ref',
    dir: 'asc',
  });

  const [assignedRows, setAssignedRows] = useState<EscalationTaskRow[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedTotal, setAssignedTotal] = useState(0);
  const [assignedTotalPages, setAssignedTotalPages] = useState(1);
  const [assignedPage, setAssignedPage] = useState(1);
  const [assignedPageSize, setAssignedPageSize] = useState(10);
  const [assignedFilters, setAssignedFilters] = useState<Record<string, string>>({});
  const [assignedSort, setAssignedSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'ref',
    dir: 'asc',
  });

  const [selectedTask, setSelectedTask] = useState<EscalationTaskRow | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [linkedCasesModal, setLinkedCasesModal] = useState<{ caseRequestId: string } | null>(null);

  const [saving, setSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const rows = tab === 'unassigned' ? unassignedRows : assignedRows;
  const loading = tab === 'unassigned' ? unassignedLoading : assignedLoading;

  const changeTab = useCallback(
    (next: 'unassigned' | 'assigned') => {
      setTab(next);
      setSearchParams({ tab: next }, { replace: true });
    },
    [setSearchParams],
  );

  const openAssignmentModal = useCallback((row: EscalationTaskRow) => {
    setSelectedTask(row);
    setAssignmentOpen(true);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    queueMicrotask(() => setUnassignedLoading(true));

    void listUnassignedEscalationTasks({
      page: unassignedPage - 1,
      pageSize: unassignedPageSize,
      columnFilters: unassignedFilters,
      sortBy: unassignedSort.key,
      sortDir: unassignedSort.dir === 'asc' ? 'ASC' : 'DESC',
      signal: controller.signal,
    })
      .then((unassigned) => {
        if (controller.signal.aborted) return;
        if (unassignedPage > unassigned.totalPages) {
          setUnassignedPage(unassigned.totalPages);
          return;
        }
        setUnassignedRows(unassigned.items);
        setUnassignedTotal(unassigned.total);
        setUnassignedTotalPages(unassigned.totalPages);
      })
      .finally(() => {
        if (!controller.signal.aborted) setUnassignedLoading(false);
      });

    return () => controller.abort();
  }, [unassignedPage, unassignedPageSize, unassignedFilters, unassignedSort.key, unassignedSort.dir, reloadToken]);

  useEffect(() => {
    if (tab !== 'assigned') return;

    const controller = new AbortController();
    queueMicrotask(() => setAssignedLoading(true));

    void listAssignedEscalationTasks({
      page: assignedPage - 1,
      pageSize: assignedPageSize,
      columnFilters: assignedFilters,
      sortBy: assignedSort.key,
      sortDir: assignedSort.dir === 'asc' ? 'ASC' : 'DESC',
      signal: controller.signal,
    })
      .then((assigned) => {
        if (controller.signal.aborted) return;
        if (assignedPage > assigned.totalPages && assigned.totalPages > 0) {
          setAssignedPage(assigned.totalPages);
          return;
        }
        setAssignedRows(assigned.items);
        setAssignedTotal(assigned.total);
        setAssignedTotalPages(assigned.totalPages);
      })
      .finally(() => {
        if (!controller.signal.aborted) setAssignedLoading(false);
      });

    return () => controller.abort();
  }, [tab, assignedPage, assignedPageSize, assignedFilters, assignedSort.key, assignedSort.dir, reloadToken]);

  const columns = useMemo<Column<EscalationTaskRow>[]>(
    () => [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        render: (r) => (
          <span className="rn">
            <Link
              {...taskCaseLink({
                caseRequestId: r.caseRequestId?.trim() || r.id,
                userTaskId: r.workflowInstanceStageUserTaskId,
              })}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {r.ref}
            </Link>
          </span>
        ),
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('tasks.shared.subFileCategory'), getValue: (r) => r.subFileCategory },
      {
        key: 'agency',
        header: t('tasks.shared.submittedAgency'),
        getValue: (r) => (r.isAgdSubmitted ? 'AGD' : r.agency),
      },
      { key: 'caseNumber', header: t('tasks.shared.caseNumber'), getValue: (r) => r.caseNumber },
      { key: 'workflowStage', header: t('tasks.shared.currentWorkflowStage'), getValue: (r) => r.workflowStage },
      {
        key: 'status',
        header: t('tasks.shared.taskStatus'),
        getValue: (r) => r.status,
        render: (r) => (
          <span className={r.status === 'Pending Assignment' ? 'pill p-pr' : 'pill p-sub'}>{r.status}</span>
        ),
      },
      ...(tab === 'unassigned'
        ? [
            {
              key: 'escalatedAt',
              header: t('tasks.escalation.escalatedAt'),
              getValue: (r: EscalationTaskRow) => r.escalatedAt ?? EMPTY_CELL,
            },
            {
              key: 'overdueTime',
              header: t('tasks.shared.overdueTime'),
              
              getValue: (r: EscalationTaskRow) => r.overdueTime || EMPTY_CELL,
              render: (r: EscalationTaskRow) =>
                r.overdueTime ? <span className="t-ov">{r.overdueTime}</span> : <span>{EMPTY_CELL}</span>,
            },
          ]
        : []),
      {
        key: 'urgency',
        header: t('tasks.shared.urgencyLevel'),
        getValue: (r) => urgencyLabel(r.urgency),
        render: (r) => {
          const label = urgencyLabel(r.urgency);
          if (!r.urgency) return <span>{label}</span>;
          return <span className={urgencyPill(r.urgency)}>{label}</span>;
        },
      },
      ...(tab === 'unassigned'
        ? [
            {
              key: 'currentAssignee',
              header: t('tasks.shared.currentAssignee'),
            
              getValue: (r: EscalationTaskRow) => r.currentAssignee ?? EMPTY_CELL,
            },
          ]
        : []),
      ...(tab === 'assigned'
        ? [
            {
              key: 'assignedOfficer',
              header: t('tasks.shared.assignedOfficer'),
              getValue: (r: EscalationTaskRow) => r.assignedOfficer ?? EMPTY_CELL,
            },
            {
              key: 'supervisor',
              header: t('tasks.shared.supervisor'),
              getValue: (r: EscalationTaskRow) => r.supervisor ?? EMPTY_CELL,
            },
          ]
        : []),
      {
        key: 'actions',
        header: t('common.actions'),
        getValue: () => '',
        searchable: false,
        sortable: false,
        render: (r) => (
          <div className="act-wrap">
            <Link
              {...taskCaseLink({
                caseRequestId: r.caseRequestId?.trim() || r.id,
                userTaskId: r.workflowInstanceStageUserTaskId,
                viewMode: !canUpdate,
                detailsTab: canUpdate ? 'cu' : undefined,
              })}
              className="act-b"
              data-tip={
                canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetailsShort')
              }
              aria-label={
                canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetailsShort')
              }
            >
              {canUpdate ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </Link>
            <LinkedCasesActionButton
              connectedAgdFileRefAvailable={r.connectedAgdFileRefAvailable === true}
              caseRequestId={r.caseRequestId ?? r.id}
              onOpen={(caseRequestId) => setLinkedCasesModal({ caseRequestId })}
            />
            {tab === 'unassigned' ? (
              <button
                type="button"
                className="act-b"
                title={t('tasks.assign.configureAssignment')}
                
                onClick={() => openAssignmentModal(r)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
                  <path d="M19 8v6" />
                  <path d="M16 11h6" />
                </svg>
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [tab, openAssignmentModal, canUpdate, taskCaseLink, t],
  );

  const saveAssignment = async (details: {
    supervisorId?: string;
    stateAttorneyId?: string;
    caseRequestCategoryAllocationUserId: string;
    userId: string;
  }) => {
    if (!selectedTask) return;
    const workflowInstanceStageUserTaskId = selectedTask.workflowInstanceStageUserTaskId?.trim() ?? '';
    if (!workflowInstanceStageUserTaskId) return;

    setSaving(true);
    try {
      const res = await assignEscalationUserTask({
        workflowInstanceStageUserTaskId,
        assignUserId: details.userId,
        assignSupervisorId: details.supervisorId,
        assignStateAttorneyId: details.stateAttorneyId,
        physicalFileMovementRequired: false,
      });
      if (!res.success) {
        setFlashToast({ message: res.message || t('tasks.assign.assignmentFailed'), variant: 'error' });
        return;
      }
      setFlashToast({ message: t('tasks.assign.assignmentSuccess'), variant: 'success' });

      setAssignmentOpen(false);
      setSelectedTask(null);

      setReloadToken((t) => t + 1);
    } finally {
      setSaving(false);
    }
  };

  const closeAssignmentDirectly = () => {
    setAssignmentOpen(false);
    setSelectedTask(null);
  };

  const beginCancel = () => {
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setAssignmentOpen(false);
    setCancelConfirmOpen(false);
    setSelectedTask(null);
  };

  return (
    <>
      <div className="bc">
        <span>{t('tasks.escalation.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('tasks.escalation.title')}</div>
          <div className="pg-sub">
            {t('tasks.escalation.subtitle')}
          </div>
        </div>
      </div>

      <div className="cr-table-wrap">
        <div className="cr-tabs-bar">
          <button type="button" className={`cr-tab ${tab === 'unassigned' ? 'on' : ''}`} onClick={() => changeTab('unassigned')}>
            {t('tasks.escalation.unassignedTab')} <span className="tab-count">{unassignedTotal}</span>
          </button>
          <button type="button" className={`cr-tab ${tab === 'assigned' ? 'on' : ''}`} onClick={() => changeTab('assigned')}>
            {t('tasks.escalation.assignedTab')} <span className="tab-count">{assignedTotal}</span>
          </button>
        </div>
        <div className="tab-panel on">
          <DataTable
            embedded
            title={t('tasks.shared.taskDetails')}
            columns={columns}
            rows={rows}
            loading={loading}
            rowKey={(r) => r.id}
            rowClassName={(r) =>
              notificationRowHighlightClass({
                status: r.taskStatus ?? r.status,
                hasNewNotification: r.hasNewNotification,
              })
            }
            defaultRowsPerPage={10}
            previousLabel={t('tasks.shared.previousBack')}
            nextLabel={t('tasks.shared.nextForward')}
            serverPaging={
              tab === 'unassigned'
                ? {
                    totalCount: unassignedTotal,
                    totalPages: unassignedTotalPages,
                    page: unassignedPage,
                    pageSize: unassignedPageSize,
                    onPageChange: (p) => setUnassignedPage(p),
                    onPageSizeChange: (s) => setUnassignedPageSize(s),
                  }
                : {
                    totalCount: assignedTotal,
                    totalPages: assignedTotalPages,
                    page: assignedPage,
                    pageSize: assignedPageSize,
                    onPageChange: (p) => setAssignedPage(p),
                    onPageSizeChange: (s) => setAssignedPageSize(s),
                  }
            }
            onFiltersChange={(filters) => {
              if (tab === 'unassigned') {
                setUnassignedPage(1);
                setUnassignedFilters(filters);
              } else {
                setAssignedPage(1);
                setAssignedFilters(filters);
              }
            }}
            fetchColumnSuggestions={(col, prefix, signal) =>
              tab === 'unassigned'
                ? fetchEscalationUnassignedColumnSuggestions(col, prefix, signal)
                : fetchEscalationAssignedColumnSuggestions(col, prefix, signal)
            }
            serverSort={{
              key: tab === 'unassigned' ? unassignedSort.key : assignedSort.key,
              dir: tab === 'unassigned' ? unassignedSort.dir : assignedSort.dir,
              onSortChange: (key, dir) => {
                if (tab === 'unassigned') {
                  setUnassignedSort({ key, dir });
                  setUnassignedPage(1);
                } else {
                  setAssignedSort({ key, dir });
                  setAssignedPage(1);
                }
              },
            }}
          />
        </div>
      </div>

      <ConfigureAssignmentModal
        isOpen={assignmentOpen}
        idPrefix="cfg-escalation"
        fileCategoryId={selectedTask?.fileCategoryId?.trim() ?? ''}
        subFileCategoryId={selectedTask?.subFileCategoryId?.trim() ?? ''}
        fileCategoryName={selectedTask?.fileCategory ?? ''}
        subFileCategoryName={selectedTask?.subFileCategory ?? ''}
        workflowInstanceStageUserTaskId={selectedTask?.workflowInstanceStageUserTaskId}
        saving={saving}
        onClose={closeAssignmentDirectly}
        onCancel={beginCancel}
        onSave={saveAssignment}
      />

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('tasks.assign.cancelAssignmentTitle')}
        message={t('tasks.assign.cancelAssignmentMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      <LinkedCasesModal
        open={linkedCasesModal != null}
        caseRequestId={linkedCasesModal?.caseRequestId ?? null}
        returnState={taskCaseReturn}
        onClose={() => setLinkedCasesModal(null)}
      />
    </>
  );
}
